import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Users, Book, Award, Activity, Bookmark, MessageSquare, FileText, School } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminUsers from './AdminUsers';
import AdminSubjects from './AdminSubjects';
import AdminStudents from './AdminStudents';
import AdminGrades from './AdminGrades';
import AdminClasses from './AdminClasses';
import AdminConseilClasseIndex from './AdminConseilClasseIndex';
import AdminConseilClasse from './AdminConseilClasse';
import AdminAppreciationsIndex from './AdminAppreciationsIndex';
import AdminAppreciations from './AdminAppreciations';
import AdminEtablissement from './AdminEtablissement';
import AdminLivretsIndex from './AdminLivretsIndex';
import AdminLivretsBatch from './AdminLivretsBatch';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalSubjects: 0,
    totalGrades: 0,
    totalClasses: 0,
    averageGrade: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      // Get students count
      const { count: studentsCount, error: studentsError } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true });

      // Get subjects count
      const { count: subjectsCount, error: subjectsError } = await supabase
        .from('subjects')
        .select('*', { count: 'exact', head: true });

      // Get classes count
      const { count: classesCount, error: classesError } = await supabase
        .from('classes')
        .select('*', { count: 'exact', head: true });

      // Get grades count and average
      const { count: gradesCount, error: gradesError } = await supabase
        .from('grades')
        .select('*', { count: 'exact', head: true });

      // Get average grade
      const { data: avgData, error: avgError } = await supabase
        .from('grades')
        .select('grade, coefficient')
        .not('grade', 'is', null); // Exclude absences from average calculation

      if (studentsError || subjectsError || gradesError || avgError || classesError) {
        console.error('Error fetching stats:', { studentsError, subjectsError, gradesError, avgError, classesError });
        return;
      }

      // Calculate average grade with coefficients
      let totalGradeValue = 0;
      let totalCoefficient = 0;
      
      avgData?.forEach(grade => {
        if (grade.grade !== null) {
          const coef = grade.coefficient || 1;
          totalGradeValue += grade.grade * coef;
          totalCoefficient += coef;
        }
      });

      const avgGrade = totalCoefficient > 0 
        ? Math.round((totalGradeValue / totalCoefficient) * 10) / 10 
        : 0;

      setStats({
        totalStudents: studentsCount || 0,
        totalSubjects: subjectsCount || 0,
        totalGrades: gradesCount || 0,
        totalClasses: classesCount || 0,
        averageGrade: avgGrade,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const DashboardHome = () => (
    <>
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
        <p className="mt-2 text-gray-600">
          Gérez tous les aspects du système de gestion des notes
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Étudiants</p>
              <p className="text-3xl font-bold mt-2 text-primary-600">
                {isLoading ? '...' : stats.totalStudents}
              </p>
            </div>
            <div className="bg-primary-100 p-3 rounded-full">
              <Users className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Classes</p>
              <p className="text-3xl font-bold mt-2 text-primary-600">
                {isLoading ? '...' : stats.totalClasses}
              </p>
            </div>
            <div className="bg-primary-100 p-3 rounded-full">
              <Bookmark className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Matières</p>
              <p className="text-3xl font-bold mt-2 text-secondary-600">
                {isLoading ? '...' : stats.totalSubjects}
              </p>
            </div>
            <div className="bg-secondary-100 p-3 rounded-full">
              <Book className="h-6 w-6 text-secondary-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Notes</p>
              <p className="text-3xl font-bold mt-2 text-accent-600">
                {isLoading ? '...' : stats.totalGrades}
              </p>
            </div>
            <div className="bg-accent-100 p-3 rounded-full">
              <Award className="h-6 w-6 text-accent-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Moyenne</p>
              <p className="text-3xl font-bold mt-2 text-success-500">
                {isLoading ? '...' : stats.averageGrade}
              </p>
            </div>
            <div className="bg-success-50 p-3 rounded-full">
              <Activity className="h-6 w-6 text-success-500" />
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion des utilisateurs</h3>
          <p className="text-gray-600 mb-4">
            En tant qu'administrateur, vous pouvez gérer les rôles des utilisateurs du système.
          </p>
          <a 
            href="/admin/users" 
            className="btn btn-outline inline-block"
          >
            Gérer les utilisateurs
          </a>
        </div>
        
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Classes</h3>
          <p className="text-gray-600 mb-4">
            Créez et gérez les programmes de formation et les classes.
          </p>
          <a 
            href="/admin/classes" 
            className="btn btn-outline inline-block"
          >
            Gérer les classes
          </a>
        </div>
        
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Matières</h3>
          <p className="text-gray-600 mb-4">
            Créez, modifiez et supprimez les matières disponibles dans le système.
          </p>
          <a 
            href="/admin/subjects" 
            className="btn btn-outline inline-block"
          >
            Gérer les matières
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Étudiants</h3>
          <p className="text-gray-600 mb-4">
            Gérez les informations des étudiants et consultez leurs notes.
          </p>
          <a 
            href="/admin/students" 
            className="btn btn-outline inline-block"
          >
            Gérer les étudiants
          </a>
        </div>
        
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
          <p className="text-gray-600 mb-4">
            Consultez et gérez toutes les notes enregistrées dans le système.
          </p>
          <a 
            href="/admin/grades" 
            className="btn btn-outline inline-block"
          >
            Gérer les notes
          </a>
        </div>
      </div>
    </>
  );

  return (
    <div className="animate-fade-in">
      <Routes>
        <Route index element={<DashboardHome />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="subjects" element={<AdminSubjects />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="grades" element={<AdminGrades />} />
        <Route path="classes" element={<AdminClasses />} />
        <Route path="etablissement" element={<AdminEtablissement />} />
        <Route path="conseil" element={<AdminConseilClasseIndex />} />
        <Route path="conseil/:classId" element={<AdminConseilClasse />} />
        <Route path="appreciations" element={<AdminAppreciationsIndex />} />
        <Route path="appreciations/:classId" element={<AdminAppreciations />} />
        <Route path="livrets" element={<AdminLivretsIndex />} />
        <Route path="livrets/:classId" element={<AdminLivretsBatch />} />
      </Routes>
    </div>
  );
};

export default AdminDashboard;